import { useLocale } from '@ecomm/data-hooks'
import { currentPromoAtom } from '@ecomm/data-promotions'
import {
  COOKIE_FIRST_LEAD_SUBMISSION,
  getCookie,
  getPartnerCookie,
  getVisitorId,
  setCookie
} from '@ecomm/shared-cookies'
import { isValidUrl, get as sessionStorageGet } from '@ecomm/utils'

import { getDeviceType } from '@simplisafe/ss-ecomm-data/utils/windowScreenSize'
import { window } from 'browser-monads-ts'
import { useCallback, useContext } from 'react'

import * as TE from 'fp-ts/lib/TaskEither'
import { pipe } from 'fp-ts/lib/function'

import type { ErrorResponse } from '@ecomm/data-simplisafe-api'
import { voidFn } from '@simplisafe/ewok'
import { isRight } from 'fp-ts/lib/Either'
import { useAtom } from 'jotai'
import { SESSION_STORAGE_FIRST_VISITED_HREF } from '../TrackingProvider/constants'
import { ReferrerUrlContext } from '../TrackingProvider/useReferrerUrl'
import { brazeSetCustomUserAttributes } from '../braze'
import { useGoogleAnalytics } from '../useGoogleAnalytics'
import {
  type LeadGenCaptureV2Props,
  type LeadGenV2ResponseProps,
  handleLeadCaptureV2Failure,
  leadGenCaptureV2
} from './leadGenCaptureV2'

/**
 * In this hook we gather data required by the new eis-leads service POST /v1/leads endpoint
 * as described here https://simplisafe.atlassian.net/wiki/spaces/EIS/pages/4056154277/EIS+Leads+Feature+Brief#POST-%2Fv1%2Fleads
 *
 * The V2 suffix in its name is because this is intended to replace the current ecomm-leads service
 * in the future. See https://simplisafe.atlassian.net/wiki/spaces/EIS/pages/4056154277/EIS+Leads+Feature+Brief#Steps
 *
 * @returns Function that takes LeadGenCaptureV2Props and can be used to call the eis-leads POST /v1/leads endpoint with the provided data.
 */
export function useLeadGenCaptureV2(
  onFail: (e: Error | ErrorResponse) => void = handleLeadCaptureV2Failure,
  onSuccess: (r?: LeadGenV2ResponseProps) => void = voidFn
) {
  const [clientId] = useGoogleAnalytics()
  const { channel } = useContext(ReferrerUrlContext)

  // We need to keep track of the user's first visited URL within a session
  // If a user navigates to a different page and submits a lead form on that page, we want to capture their channel based on the first page that they visited
  // We can't rely on referrer in this case, as it will be set to the SimpliSafe Site URL once the user navigates to another page
  const firstVisitedHref = sessionStorageGet(SESSION_STORAGE_FIRST_VISITED_HREF)
  const leadFirstUrl = isValidUrl(firstVisitedHref)
    ? new URL(firstVisitedHref)
    : window.location
  const { pathname: urlPath, search } = leadFirstUrl

  const locale = useLocale()

  // Read from currentPromoAtom instead of useCurrentPromo to avoid circular dependency w/ @ecomm/promotions-hooks
  const [
    {
      content: { promoCode, promoCodeWithMonitoring }
    }
  ] = useAtom(currentPromoAtom)

  return useCallback(
    (data: LeadGenCaptureV2Props, subspace = false) => {
      const isFirstSubmission =
        (getCookie(COOKIE_FIRST_LEAD_SUBMISSION) ?? 'true') === 'true'
      const partnerData = getPartnerCookie()
      const vid = getVisitorId()
      const deviceType = getDeviceType().toLowerCase()
      const queryParams = new URLSearchParams(search)
      const utmSource = queryParams.get('utm_source') || ''
      const utmMedium = queryParams.get('utm_medium') || ''
      const utmCampaign = queryParams.get('utm_campaign') || ''
      const country = locale === 'en-US' ? 'US' : 'GB'

      const siteMetadata = {
        rudderstackId: clientId,
        partner: partnerData?.partnerName,
        vid,
        channel,
        urlPath,
        deviceType,
        promoOffer: promoCodeWithMonitoring || promoCode || 'NO_CODE',
        utmSource,
        utmMedium,
        utmCampaign,
        timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone
      }
      const firstPromoSeen = siteMetadata.promoOffer
      const lastPromoSeen = data?.siteMetadata?.promoOffer || firstPromoSeen
      pipe(
        TE.tryCatch(
          leadGenCaptureV2(
            {
              country,
              ...data,
              siteMetadata: {
                ...siteMetadata,
                ...data.siteMetadata
              }
            },
            subspace
          ),
          reason => new Error(String(reason))
        ),
        TE.chainFirst(_ =>
          TE.rightIO(() => {
            if (isFirstSubmission) {
              brazeSetCustomUserAttributes({
                'last promo seen': lastPromoSeen,
                'first seen promo': firstPromoSeen
              })
              setCookie(COOKIE_FIRST_LEAD_SUBMISSION, 'false')
            } else {
              brazeSetCustomUserAttributes({
                'last promo seen': lastPromoSeen
              })
            }
          })
        ),
        TE.chain(result =>
          isRight(result) ? TE.right(result.right) : TE.left(result.left)
        ),
        TE.match(onFail, onSuccess)
      )()
    },
    [
      search,
      locale,
      clientId,
      channel,
      promoCode,
      promoCodeWithMonitoring,
      urlPath,
      onFail,
      onSuccess
    ]
  )
}
